/**
 * Which of the two looks the interface wears.
 *
 * Pure, and in the domain layer, for the same reason the palette is: choosing a theme is
 * a decision about the *preference*, not about the DOM. Splitting it out means the part
 * worth getting right — what a stored preference resolves to, and what pressing the
 * button does next — can be tested without a browser, and the UI module is left with
 * nothing but wiring.
 *
 * The distinction that matters here is between the *choice* and the *theme*. A user picks
 * one of three things, and one of those three is "whatever the system says"; what is
 * actually applied is always one of two.
 */

/** What the interface is actually wearing. */
export type Theme = 'light' | 'dark';

/**
 * What the user asked for.
 *
 * `system` is a first-class choice rather than the absence of one: a user who has tried
 * light and gone back to following their system has expressed a preference, and it should
 * survive a reload the same way the other two do.
 */
export type ThemeChoice = Theme | 'system';

/**
 * The order the button cycles through.
 *
 * `system` first, because it is where everyone starts and the one press that gets back to
 * it should be predictable. Light before dark so that pressing the button on a machine
 * set to dark visibly does something on the first press.
 */
export const THEME_CHOICES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/** Where the choice is kept between visits. */
export const THEME_STORAGE_KEY = '3dpreview:theme';

/**
 * Read back a stored choice, falling back to following the system.
 *
 * Takes `string | null` because that is what `localStorage.getItem` gives, and anything
 * unrecognised is treated as absent: the value may have been written by an older version
 * or edited by hand, and neither is a reason to fail to render a page.
 */
export function parseThemeChoice(value: string | null | undefined): ThemeChoice {
  return THEME_CHOICES.find((choice) => choice === value) ?? 'system';
}

/** What a choice comes to, given what the system is asking for. */
export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): Theme {
  if (choice === 'system') return systemPrefersDark ? 'dark' : 'light';
  return choice;
}

/** The choice one press of the button moves to. */
export function nextThemeChoice(choice: ThemeChoice): ThemeChoice {
  const at = THEME_CHOICES.indexOf(choice);

  // An unknown choice cannot leave the button stuck: `indexOf` gives -1, and the next
  // one along from that is the first.
  return THEME_CHOICES[(at + 1) % THEME_CHOICES.length] ?? 'system';
}

/** What the button says, and what it says to a screen reader. */
export interface ThemeLabel {
  /** Shown on the button. */
  readonly text: string;
  /**
   * The accessible name, and the tooltip.
   *
   * Spells out both the current state and what pressing will do. The visible text can
   * only carry one of those, and for a control that cycles rather than toggles, knowing
   * where the next press lands is most of what makes it usable.
   */
  readonly description: string;
}

export function describeThemeChoice(choice: ThemeChoice, resolved: Theme): ThemeLabel {
  const next = nextThemeChoice(choice);
  const action = `Switch to ${next === 'system' ? 'automatic' : next}.`;

  if (choice === 'system') {
    return {
      text: 'Theme: Auto',
      description: `Theme follows your system, which is set to ${resolved}. ${action}`,
    };
  }

  return {
    text: `Theme: ${choice === 'light' ? 'Light' : 'Dark'}`,
    description: `Theme is set to ${choice}. ${action}`,
  };
}
